import * as React from "react";
import { Alert, Box, Button, Card, Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import {
	ArrowsClockwiseIcon,
	ArrowSquareOutIcon,
	BookOpenTextIcon,
	ListMagnifyingGlassIcon,
	TrashIcon,
} from "@phosphor-icons/react";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate } from "react-router-dom";

import { IntakeKey, intakeKeysKeys, useRevokeIntakeKey, useRotateIntakeKeySecret } from "@/hooks/api/use-intake-keys";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { RouterLink } from "@/components/core/link";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreateIntakeKey } from "./create";
import { SecretDialog } from "./secret-dialog";
import { SetupInstructionsDialog } from "./setup-instructions-dialog";

export function WebsiteIntake(): React.JSX.Element {
	const navigate = useNavigate();

	const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
	const [selectedKey, setSelectedKey] = React.useState<IntakeKey | null>(null);
	const [issuedSecret, setIssuedSecret] = React.useState<{ secret: string; keyId: string } | null>(null);
	const [instructionsFor, setInstructionsFor] = React.useState<IntakeKey | null>(null);

	const {
		items: keys,
		isLoading,
		totalItems,
		refetchData: fetchKeys,
	} = useCachedTableStateWithUrl<IntakeKey>({
		queryKey: intakeKeysKeys.lists(),
		endpoint: "/intake-keys",
	});

	const revokeMutation = useRevokeIntakeKey();
	const rotateMutation = useRotateIntakeKeySecret();

	const {
		data: keyToRevoke,
		open: isRevokeModalOpen,
		handleClose: handleRevokeModalClose,
		handleOpen: handleRevokeModalOpen,
	} = useDialog<IntakeKey | null>();

	const {
		data: keyToRotate,
		open: isRotateModalOpen,
		handleClose: handleRotateModalClose,
		handleOpen: handleRotateModalOpen,
	} = useDialog<IntakeKey | null>();

	const handleRevoke = async () => {
		if (keyToRevoke?.id) {
			await revokeMutation.mutateAsync(keyToRevoke.id);
			handleRevokeModalClose();
		}
	};

	const handleRotate = async () => {
		if (keyToRotate?.id) {
			const issued = await rotateMutation.mutateAsync(keyToRotate.id);
			handleRotateModalClose();
			setIssuedSecret({ secret: issued.secret, keyId: issued.key.keyId });
		}
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: { id: "name", label: "Name", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "name",
			},
			{
				header: { id: "keyId", label: "Key ID", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "keyId",
				getCellValue: (row: IntakeKey) => (
					<Box component="code" sx={{ fontFamily: "monospace", fontSize: 13 }}>
						{row.keyId}
					</Box>
				),
			},
			{
				header: { id: "status", label: "Status", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "enabled",
				getCellValue: (row: IntakeKey) => {
					if (row.expired) return <Chip label="Expired" size="small" variant="outlined" color="error" />;
					if (!row.enabled) return <Chip label="Disabled" size="small" variant="outlined" color="default" />;
					return <Chip label="Active" size="small" variant="filled" color="success" />;
				},
			},
			{
				header: { id: "allowedCountries", label: "Countries", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "allowedCountries",
				getCellValue: (row: IntakeKey) =>
					row.allowedCountries.length > 0 ? (
						<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
							{row.allowedCountries.map((country) => (
								<Chip key={country} label={country} size="small" variant="outlined" color="default" />
							))}
						</Box>
					) : (
						<Tooltip title="Any country can be dialed through this key. This is the main toll-fraud exposure if the secret leaks.">
							<Chip label="Any" size="small" variant="outlined" color="warning" sx={{ cursor: "help" }} />
						</Tooltip>
					),
			},
			{
				header: { id: "allowedIps", label: "Source IPs", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "allowedIps",
				getCellValue: (row: IntakeKey) =>
					row.allowedIps.length > 0 ? (
						<Tooltip title={row.allowedIps.join(", ")}>
							<Chip label={`${row.allowedIps.length} allowed`} size="small" variant="outlined" color="primary" />
						</Tooltip>
					) : (
						<Chip label="Any" size="small" variant="outlined" color="default" />
					),
			},
			{
				header: { id: "fieldMap", label: "Fields", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "fieldMap",
				getCellValue: (row: IntakeKey) => {
					const pairs = Object.entries(row.fieldMap ?? {});
					if (pairs.length === 0) {
						return (
							<Tooltip title="No submitted field becomes a contact attribute through this key.">
								<Chip label="None" size="small" variant="outlined" color="warning" sx={{ cursor: "help" }} />
							</Tooltip>
						);
					}

					// A field the website sends is silently dropped unless it is mapped
					// here, so the map is worth seeing without opening the drawer.
					return (
						<Tooltip title={pairs.map(([from, to]) => (from === to ? from : `${from} \u2192 ${to}`)).join(", ")}>
							<Chip label={`${pairs.length} mapped`} size="small" variant="outlined" color="default" sx={{ cursor: "help" }} />
						</Tooltip>
					);
				},
			},
			{
				header: { id: "rateLimitPerHour", label: "Rate limit", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "rateLimitPerHour",
				getCellValue: (row: IntakeKey) => `${row.rateLimitPerHour}/hour`,
			},
			{
				header: { id: "lastUsedAt", label: "Last used", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "lastUsedAt",
				getCellValue: (row: IntakeKey) => (row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleString() : "Never"),
			},
			{
				header: { id: "actions", label: "", width: 100, align: "left", sx: { fontWeight: "600" } },
				accessorKey: "actions",
				getCellValue: (row: IntakeKey) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Setup instructions",
									icon: <BookOpenTextIcon width={20} height={20} weight="fill" />,
									onClick: () => setInstructionsFor(row),
								},
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedKey(row);
										setIsDrawerOpen(true);
									},
								},
								{
									label: "Rotate secret",
									icon: <ArrowsClockwiseIcon width={20} height={20} weight="fill" />,
									onClick: () => handleRotateModalOpen(row),
								},
								{
									label: "Revoke",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleRevokeModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, [handleRevokeModalOpen, handleRotateModalOpen]);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Website</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Let a website &ldquo;call me&rdquo; form queue a callback on one of your campaigns
							</Typography>
						</Box>
						<Stack direction="row" spacing={1}>
							<Button
								startIcon={<ListMagnifyingGlassIcon />}
								variant="outlined"
								onClick={() => navigate("/settings/website-intake/requests")}
							>
								Recent requests
							</Button>
							<Button
								startIcon={<PlusIcon />}
								variant="contained"
								onClick={() => {
									setSelectedKey(null);
									setIsDrawerOpen(true);
								}}
							>
								New key
							</Button>
						</Stack>
					</Stack>

					<Alert severity="info" icon={<ArrowSquareOutIcon />}>
						<Typography variant="body2">
							Requests must be signed <strong>server-side</strong>. Your website&rsquo;s backend signs the payload with
							the secret and forwards it - the secret must never be shipped to the browser. The campaign must use a{" "}
							<strong>static</strong> contact group.{" "}
							<Link component={RouterLink} href="/settings/website-intake/requests" variant="body2">
								See recent requests
							</Link>{" "}
							to confirm submissions are arriving.
						</Typography>
					</Alert>

					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={keys}
								refetchData={fetchKeys}
								isLoading={isLoading}
								totalItems={totalItems}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isRevokeModalOpen}
				onClose={handleRevokeModalClose}
				onDelete={handleRevoke}
				title="Revoke intake key"
				description="The website using this key will stop being able to submit callback requests immediately."
				deleteText="Revoke"
			/>

			<DeleteModal
				open={isRotateModalOpen}
				onClose={handleRotateModalClose}
				onDelete={handleRotate}
				title="Rotate signing secret"
				description="A new secret is issued and the current one stops working immediately. Update your website config as soon as you have the new value."
				deleteText="Rotate"
			/>

			<CreateIntakeKey
				intakeKey={selectedKey}
				isDrawerOpen={isDrawerOpen}
				onDrawerClose={() => {
					setIsDrawerOpen(false);
					setTimeout(() => setSelectedKey(null), 300);
				}}
				onSecretIssued={setIssuedSecret}
			/>

			<SecretDialog
				open={!!issuedSecret}
				onClose={() => setIssuedSecret(null)}
				secret={issuedSecret?.secret ?? null}
				keyId={issuedSecret?.keyId ?? null}
			/>

			<SetupInstructionsDialog
				open={!!instructionsFor}
				onClose={() => setInstructionsFor(null)}
				intakeKey={instructionsFor}
			/>
		</React.Fragment>
	);
}
